/**
 * Which component renders which surface.
 *
 * The server answers every /app path with a template name and its data. This
 * table maps that name to a component, and anything it does not name falls
 * through to the payload renderer, which shows the real data with its real
 * labels rather than a blank page.
 *
 * The fallback is the design decision worth defending. A port that routed an
 * unmapped surface to a "not implemented" page would have shipped one working
 * screen at a time over weeks, and nobody could have reviewed the flows in
 * between. Here every one of the platform's surfaces renders from the first
 * build, and the hand-built screens replace the fallback where layout earns its
 * keep. The handover guide lists which are which, so nobody mistakes a rendered
 * payload for a finished screen.
 */
import type { Dict, Grant, ListPayload, SectionMeta } from '../api/types'
import { GenericRecord } from './GenericRecord'
import { Inbox } from './Inbox'
import { Introspect, AutoNote } from './Introspect'
import { ListScreen } from './ListScreen'
import { ConsentDenied, Denied, Placeholder, ReasonGate } from './Simple'
import { AgencyDashboard } from './dashboards/Agency'
import { CustomerDashboard } from './dashboards/Customer'
import { PlatformDashboard } from './dashboards/Platform'
import { ProviderDashboard } from './dashboards/Provider'
import { ApplicationRecord } from './records/Application'
import { CompareOffers } from './records/Compare'
import { CustomerRecord } from './records/Customer'
import { RequestRecord } from './records/Request'
import { SettingRecord } from './records/Setting'
import { PageHead } from '../components/PageHead'

type Screen = (args: { data: Dict }) => JSX.Element

/** Surfaces with a hand-built screen. */
const SCREENS: Record<string, Screen> = {
  'list.html': ({ data }) => <ListScreen initial={data.d as ListPayload} />,
  'placeholder.html': ({ data }) => <Placeholder data={data} />,
  'denied.html': ({ data }) => <Denied detail={data.detail as string} />,
  'consent_denied.html': ({ data }) => <ConsentDenied data={data} />,
  'reason.html': ({ data }) => <ReasonGate data={data} />,
  'record.html': ({ data }) => <GenericRecord data={data} />,

  'inbox.html': ({ data }) => (
    <Inbox data={data as never} section={data.section as SectionMeta}
           grant={data.grant as Grant} />
  ),

  'dashboard.customer': ({ data }) => (
    <CustomerDashboard data={data.data as Dict} section={data.section as SectionMeta} />
  ),
  'dashboard.provider': ({ data }) => (
    <ProviderDashboard data={data.data as Dict} section={data.section as SectionMeta} />
  ),
  'dashboard.agency': ({ data }) => (
    <AgencyDashboard data={data.data as Dict} section={data.section as SectionMeta} />
  ),
  'dashboard.platform': ({ data }) => (
    <PlatformDashboard data={data.data as Dict} section={data.section as SectionMeta} />
  ),

  'msme/customer.html': ({ data }) => <CustomerRecord data={data} />,
  'admin/setting.html': ({ data }) => <SettingRecord data={data} />,
  'msme/request.html': ({ data }) => <RequestRecord data={data} />,
  'provider/application.html': ({ data }) => <ApplicationRecord data={data} />,
  'msme/compare.html': ({ data }) => <CompareOffers data={data} />,
}

/** Surfaces whose payload the fallback renders, and the title to give them. */
export function Surface({ template, data }: { template: string; data: Dict }) {
  const hand = SCREENS[template]
  if (hand) return hand({ data })

  const section = (data.section || null) as SectionMeta | null
  const sectionKey = section?.section_key || 'home.task_inbox'

  return (
    <>
      <PageHead section={section} grant={data.grant as Grant | undefined}
                auditLink={(data.audit_link as string) || null}
                title={data.title as string | undefined} />
      <Introspect data={data} sectionKey={sectionKey}
                  note={<AutoNote section={section} />} />
    </>
  )
}

/** Which templates have a hand-built screen. Read by the smoke suite, so the
 *  coverage figure in the handover guide is measured rather than claimed. */
export const HAND_BUILT = Object.keys(SCREENS)
